import { getLocalStorageString, loadScript } from "../../../../utils";
import promiseWithTimeout from "../../../../utils/timeout";
import { PageType } from "../../../dataLayer";
import { filterRelevance } from "../../../meta-api";
import requestArticleMetadata from "../../../meta-api/article-meta";
import Service from "../../../service";
import { cleanEmptyValues } from "../../dmp-format";
const defaultRelevancyLimits = {
    iabTier1: {
        min: 0,
        max: 100,
    },
    iabTier2: {
        min: 10,
        max: 100,
    },
    semantics: {
        min: 10,
        max: 100,
    },
};
class AlmaPermutiveService extends Service {
    constructor(context, config) {
        super("AlmaPermutive", context);
        this.permutiveReady = false;
        this.userIdentified = false;
        this.permutiveHasConsent = false;
        this.consentListenerSet = false;
        this.configuration = {
            ...config,
            defaults: {
                ...config.defaults,
                relevancy: {
                    relevancyLimits: config.defaults?.relevancy?.relevancyLimits ??
                        defaultRelevancyLimits,
                },
            },
        };
    }
    async init() {
        this.logger.logInfo(`Started`);
    }
    collectData() {
        const collect = async () => {
            this.logger.logInfo(`Data collection requested`);
            let customProps;
            try {
                this.logger.logInfo("Checking for preDataCollection hook");
                if (this.configuration.preDataCollection) {
                    this.logger.logInfo("Found preDataCollection, awaiting it");
                    const result = await this.configuration.preDataCollection(this.context);
                    if (typeof result === "object" && "reason" in result) {
                        this.logger.logInfo(`DMP data collection cancelled: ${result.reason}`);
                        return;
                    }
                    this.logger.logInfo(`preDataCollection returned ${JSON.stringify(result, null, 2)}`);
                    customProps = result.props;
                }
            }
            catch (e) {
                this.logger.logError(`Error occured when calling preDataCollection: ${e}`);
            }
            const hasConsent = await this.getPermutiveConsent();
            if (!hasConsent) {
                this.logger.logInfo("Permutive lacks consent from user, do nothing");
                return;
            }
            if (this.permutiveReady === false) {
                await this.initPermutive(this.configuration.organizationId, this.configuration.workspaceId, this.configuration.workspaceApiKey);
                this.permutiveHasConsent = true;
                window.permutive?.consent({ optin: true, token: "CONSENT_CAPTURED" });
            }
            if (this.permutiveHasConsent === false) {
                this.logger.logInfo(`Permutive lacks consent`);
                return;
            }
            if (this.permutiveReady === false) {
                this.logger.logInfo(`Permutive not ready, cancel data collection`);
                return;
            }
            try {
                if (this.userIdentified === false) {
                    this.logger.logInfo(`Trying to identify user first...`);
                    let ids = [];
                    const { cdime: emailSha256, alma_id: almaId, amid, } = this.context.services.dataLayerService?.dataLayer ?? {};
                    // email_sha256
                    if (typeof emailSha256 === "string" &&
                        emailSha256.length > 0 &&
                        emailSha256.match(/^[0-9a-f]{64}$/i)) {
                        ids = [
                            ...ids,
                            { tag: "email_sha256", id: emailSha256, priority: 0 },
                        ];
                    }
                    // alma_id
                    if (typeof almaId === "string" && almaId.length > 0) {
                        ids = [...ids, { tag: "alma_id", id: almaId, priority: 1 }];
                    }
                    // amid
                    if (typeof amid === "string" && amid.length > 0) {
                        ids = [...ids, { tag: "amid", id: amid, priority: 2 }];
                    }
                    if (ids.length > 0) {
                        this.identifyUser(ids);
                    }
                    else {
                        this.logger.logInfo(`No ids to identify with`);
                    }
                }
            }
            catch (e) {
                this.logger.logError(`Permutive identify failed: ${e}`);
            }
            try {
                this.context.core?.recordEvent("permutive.snapshot.start", window.performance.now());
                const snapshot = await this.createDataLayerSnapshot(customProps);
                this.context.core?.recordEvent("permutive.snapshot.end", window.performance.now());
                if (!snapshot) {
                    this.logger.logError(`Cancelling data collection - could not form DMP data`);
                    return;
                }
                await this.trackPageView(snapshot);
            }
            catch (e) {
                this.logger.logError(`Permutive data collection failed due to: ${e}`);
            }
        };
        const collectionTimeout = this.configuration.defaults?.waitBeforeCollect ?? 0;
        if (this.collectionRequestTimeout) {
            this.logger.logInfo(`Clearing stale page view request`);
            window.clearTimeout(this.collectionRequestTimeout);
            this.collectionRequestTimeout = undefined;
        }
        this.logger.logInfo(`Pageview requested, executing in ${collectionTimeout} ms`);
        this.context.core?.recordEvent("permutive.pageview_requested", performance.now());
        this.collectionRequestTimeout = window.setTimeout(collect, collectionTimeout);
    }
    async getSegmentsAsync() {
        this.logger.logInfo(`Current segments requested`);
        if (this.permutiveReady === false) {
            this.logger.logInfo("Permutive lacks consent, return empty");
            return [];
        }
        return promiseWithTimeout(200, new Promise((resolve) => {
            window.permutive?.segments((segments) => {
                this.logger.logInfo(`Current segments received: ${segments.join(",")}`);
                resolve(segments);
            });
        }), "Timeout of 200 ms exceeded while fetching segments");
    }
    getSegmentsSync() {
        this.logger.logInfo(`Current segments requested (sync)`);
        const segmentString = getLocalStorageString("_papns");
        if (segmentString) {
            const segments = JSON.parse(segmentString);
            this.logger.logInfo(`Current segments received (sync): ${segments.join(",")}`);
            return segments;
        }
        return [];
    }
    async createDataLayerSnapshot(customProps) {
        this.logger.logInfo(`Create data layer snapshot`);
        if (!this.context.services.dataLayerService) {
            throw new Error("Data layer service not available");
        }
        const { dataLayer } = this.context.services.dataLayerService;
        const dmpBsStart = window.performance.now();
        const isBrandSafe = await this.context.services.brandsafetyService?.isSafe("dmp");
        if (typeof isBrandSafe === "boolean") {
            this.context.core?.recordEvent("permutive.bs.start", dmpBsStart);
            this.context.core?.recordEvent("permutive.bs.end", window.performance.now());
        }
        let filteredMetaData = {};
        if (dataLayer.pageType === PageType.Article &&
            typeof dataLayer.cid === "string") {
            const limits = this.configuration.defaults?.relevancy?.relevancyLimits ??
                defaultRelevancyLimits;
            this.logger.logInfo(`Filtering DMP meta: ${JSON.stringify(limits, null, 2)}`);
            const metaData = await requestArticleMetadata(this.context);
            filteredMetaData = {
                iabTier1: filterRelevance(metaData?.iabTier1 ?? [], limits.iabTier1.min, limits.iabTier1.max),
                iabTier2: filterRelevance(metaData?.iabTier2 ?? [], limits.iabTier2.min, limits.iabTier2.max),
                leikiHighDef: filterRelevance(metaData?.leikiHighDef ?? [], limits.semantics.min, limits.semantics.max),
            };
        }
        const defaultPageProps = [
            {
                name: "iabTier1",
                brandSafety: true,
                resolve: () => {
                    return filteredMetaData.iabTier1;
                },
            },
            {
                name: "iabTier2",
                brandSafety: true,
                resolve: () => {
                    return filteredMetaData.iabTier2;
                },
            },
            {
                name: "semantics",
                brandSafety: true,
                resolve: () => {
                    return filteredMetaData.leikiHighDef;
                },
            },
            {
                name: "primaryCategory",
                resolve: (context) => {
                    return context.services.dataLayerService?.dataLayer.primaryCategory;
                },
            },
            {
                name: "subCategory",
                resolve: (context) => {
                    return context.services.dataLayerService?.dataLayer.subCategory1;
                },
            },
            {
                name: "tags",
                resolve: (context) => {
                    return context.services.dataLayerService?.dataLayer.pageTags;
                },
            },
            {
                name: "pageType",
                resolve: (context) => {
                    return context.services.dataLayerService?.dataLayer.pageType;
                },
            },
        ];
        try {
            // Split category to we can merge any extra stuff
            const { page = [], ...otherProps } = { ...customProps };
            const pageviewData = {
                page: [...defaultPageProps, ...(page ?? [])],
                ...otherProps,
            };
            // Resolve all and reduce to key-value object
            const cleanData = (await Promise.all(Object.entries(pageviewData).map(async ([k, v]) => {
                return { [k]: await this.resolveDataLayer(v, isBrandSafe) };
            }))).reduce((k, v) => {
                const propKey = Object.keys(v)[0];
                return { ...k, [propKey]: v[propKey] };
            }, {});
            this.logger.logInfo(`Data layer snapshot: ${JSON.stringify(cleanData, null, 2)}`);
            return cleanData;
        }
        catch (e) {
            this.logger.logInfo(`DMP Data layer snapshot failed: ${e}`);
        }
        return undefined;
    }
    async resolveDataLayer(dmpProperty, isBrandSafe) {
        this.logger.logInfo(`Resolving partial DMP data layer. ${dmpProperty.length} props`);
        const brandsafetyfyValue = (value) => {
            if (typeof value === "string") {
                return `${value}-bs`;
            }
            return value;
        };
        const resolveProps = async (props) => {
            const values = (await Promise.all(props.map(async (prop) => {
                try {
                    let result = await prop.resolve(this.context);
                    if (prop.brandSafety && isBrandSafe === false) {
                        if (Array.isArray(result)) {
                            result = result.map(brandsafetyfyValue);
                        }
                        else {
                            result = [result].map(brandsafetyfyValue)?.[0];
                        }
                    }
                    return {
                        key: prop.name,
                        value: result,
                    };
                }
                catch (e) {
                    this.logger.logError(`Unable to resolve ${prop.name} due to ${e}`);
                    return { key: prop.name, value: undefined };
                }
            }))).reduce((acc, prop) => {
                return { ...acc, [prop.key]: prop.value };
            }, {});
            return cleanEmptyValues(values);
        };
        return resolveProps(dmpProperty);
    }
    async trackPageView(snapshot) {
        if (snapshot) {
            // Wrap in prop `page`, this is intentional
            const permutivePayload = { page: snapshot };
            this.logger.logInfo(`Send pageview to Permutive with payload: ${JSON.stringify(permutivePayload, null, 2)}`);
            window.permutive?.addon("web", permutivePayload);
        }
    }
    identifyUser(ids) {
        if (this.userIdentified === false) {
            this.logger.logInfo(`Sending user ids to Permutive: ${ids
                .map((id) => `${id.tag}:${id.id}`)
                .join(",")}`);
            window.permutive?.identify(ids);
            this.userIdentified = true;
        }
        this.logger.logInfo(`User already identified`);
    }
    async getPermutiveConsent() {
        const consent = await this.context.services.almaCmpService?.getPublisherConsents();
        const hasConsent = (c) => {
            if (c?.standard[1] === true &&
                c.standard[3] === true &&
                c.standard[4] === true &&
                c.custom[1] === true &&
                c.specialFeature[2] === true &&
                c.vendors[361] === true) {
                return true;
            }
            return false;
        };
        if (!this.consentListenerSet) {
            const consentChangedEvent = "alma_cmp_v2_has_optedin";
            this.logger.logInfo(`Listening for changes with ${consentChangedEvent}`);
            window.addEventListener(consentChangedEvent, async (event) => {
                this.logger.logInfo(`Consent changed, update`);
                const result = hasConsent(event.detail);
                if (this.permutiveHasConsent !== result) {
                    this.permutiveHasConsent = result;
                    window.permutive?.consent({
                        optin: result,
                        token: "CONSENT_CAPTURED",
                    });
                    if (result === true && this.permutiveReady === false) {
                        await this.initPermutive(this.configuration.organizationId, this.configuration.workspaceId, this.configuration.workspaceApiKey);
                    }
                }
            }, false);
            this.consentListenerSet = true;
        }
        return hasConsent(consent);
    }
    async initPermutive(organizationId, workspaceId, workspaceApiKey, environment = "production") {
        this.logger.logInfo(`Init Permutive`);
        if (!organizationId || !workspaceId || !workspaceApiKey) {
            throw new Error("Missing Permutive ids");
        }
        window.permutive = window.permutive || {};
        window.permutive.q = [];
        window.permutive.config = {
            apiKey: workspaceApiKey,
            environment,
        };
        for (let o = [
            "addon",
            "identify",
            "track",
            "trigger",
            "query",
            "segment",
            "segments",
            "ready",
            "on",
            "once",
            "user",
            "consent",
        ], r = 0; r < o.length; r += 1) {
            const t = o[r];
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const pgi = window.permutive;
            pgi[t] = ((e) => {
                return (...rest) => {
                    const i = Array.prototype.slice.call(rest, 0);
                    pgi.q.push({ functionName: e, arguments: i });
                };
            })(t);
        }
        this.permutiveReady = true;
        loadScript(`https://${organizationId}.edge.permutive.app/${workspaceId}-web.js`, window);
    }
}
export default AlmaPermutiveService;
//# sourceMappingURL=permutive.js.map